import { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../admin/api';
import { fDateTime } from '../../utils/formatTime';
import { AdminConfirmationDialog, AdminDataTable } from '../../sections/admin/shared';
import { AdminRouteLayout, RequestState, useAsyncState } from './AdminRoutePageShared';

export default function ConfigAuditPage() {
  const { enqueueSnackbar } = useSnackbar();
  const audit = useAsyncState([]);
  const { state: auditState, setLoading: setAuditLoading, setData: setAuditData, setError: setAuditError } = audit;
  const [pendingRollback, setPendingRollback] = useState(null);

  const loadAudit = useCallback(async () => {
    setAuditLoading();
    try {
      const response = await fetchAdmin('/config/audit');
      setAuditData(response.changes || []);
    } catch (error) {
      setAuditError(toErrorMessage(error));
    }
  }, [setAuditData, setAuditError, setAuditLoading]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const rollback = async () => {
    if (!pendingRollback) {
      return;
    }

    try {
      await postAdmin(`/config/rollback/${pendingRollback.id}`);
      enqueueSnackbar('Config rollback applied', { variant: 'success' });
      setPendingRollback(null);
      await loadAudit();
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <AdminRouteLayout title="Config Audit" breadcrumbTitle="Config">
      <RequestState loading={auditState.loading} error={auditState.error}>
        <AdminDataTable
          title="Config Audit History"
          description="Chronological config-item change history only. This endpoint does not include feature-flag audit events."
          columns={[
            { field: 'createdAt', headerName: 'Changed At' },
            { field: 'groupKey', headerName: 'Group Key' },
            { field: 'itemKey', headerName: 'Item Key' },
            { field: 'changedBy', headerName: 'Changed By' },
            { field: 'oldValue', headerName: 'Old Value' },
            { field: 'newValue', headerName: 'New Value' },
            { field: 'reason', headerName: 'Reason' },
            {
              field: 'actions',
              headerName: 'Actions',
              render: (row) => (
                <Button size="small" color="error" variant="outlined" onClick={() => setPendingRollback(row.original)}>
                  Rollback
                </Button>
              ),
            },
          ]}
          rows={(auditState.data || []).map((row) => ({
            id: row.id,
            original: row,
            createdAt: fDateTime(row.created_at),
            groupKey: row.group_key,
            itemKey: row.item_key,
            changedBy: row.changed_by || 'Unknown',
            oldValue: row.old_value ?? 'null',
            newValue: row.new_value ?? 'null',
            reason: row.reason || 'No reason provided',
          }))}
          emptyTitle="No config audit rows"
          emptyDescription="The config audit endpoint returned no change history."
        />
      </RequestState>

      <AdminConfirmationDialog
        open={Boolean(pendingRollback)}
        title="Confirm Config Rollback"
        description={
          pendingRollback
            ? `Rollback ${pendingRollback.group_key}.${pendingRollback.item_key} from ${fDateTime(pendingRollback.created_at)}? This will immediately restore the previous live config value.`
            : 'Rollback the selected config change.'
        }
        confirmLabel="Apply Rollback"
        onClose={() => setPendingRollback(null)}
        onConfirm={rollback}
      />
    </AdminRouteLayout>
  );
}
